import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@services/auth';
import { useI18n, type Language } from '../../i18n';
import { usePageActivityTracker } from '@services/activityTracker';

type Lang = Language;

const translations: Record<Lang, Record<string, string>> = {
	en: {
		pageTitle: 'Savings Calculator',
		pageSubtitle: 'Calculate how much money and time your company can save by using HortEra. Eliminate site visits, reduce planning time, cut revisions, and streamline your workflow.',
		pageHint: 'Enter your current costs • See your savings',
		modeBasic: 'Basic',
		modeAdvanced: 'Advanced',
		calculationMode: 'Calculation mode',
		basicModeHint: 'Estimates full monthly workflow cost as potential savings.',
		advancedAssumptions: 'Savings assumptions',
		advancedModeHint: 'Adjust how much HortEra reduces each cost area. Defaults are conservative estimates.',
		reductionPct: 'reduction',
		planningReduction: 'Planning time',
		consultationReduction: 'Consultation',
		quotePrepReduction: 'Quote preparation',
		siteVisitReduction: 'Site visits (planning)',
		revisionReduction: 'Revisions',
		materialSourcingReduction: 'Material sourcing',
		softwareReduction: 'Software subscriptions',
		otherCostsReduction: 'Other planning costs',
		returnVisitsSection: 'Return / follow-up visits',
		returnVisitsPerProject: 'Return visits per project',
		hoursPerReturnVisit: 'Hours per return visit',
		returnVisitReduction: 'Return visit reduction',
		returnVisitHint: 'Extra trips for measurements, revisions, or clarifications after the first visit.',
		upliftSection: 'Capacity & conversion',
		extraProjectsPerMonth: 'Extra projects per month',
		upliftHint: 'Projects you can win or handle with the same team thanks to faster quoting and planning.',
		marketPreset: 'Market defaults',
		marketGermany: 'Germany',
		marketAustria: 'Austria',
		marketCroatia: 'Croatia',
		estimatedMonthlySavings: 'Estimated monthly savings:',
		upliftSavings: 'Capacity / conversion:',
		returnVisitSavings: 'Return visits:',
		returnVisitsCost: 'Return visits (current):',
		howWeCalculateAdvanced: 'Advanced mode applies your reduction percentages to each cost area, adds return-visit savings, and optional extra projects from faster workflows. Figures are estimates.',
		yourCurrentCosts: 'Your Current Costs',
		quickStart: 'Quick Start (Load Preset)',
		smallCompany: 'Small Company',
		mediumCompany: 'Medium Company',
		largeCompany: 'Large Company',
		workersLabel: 'Number of Workers / Team Members',
		projectsPerMonth: 'Projects per Month',
		hourlyRate: 'Average Hourly Rate (€)',
		timeCostsPerProject: 'Time Costs per Project',
		planningHours: 'Planning Hours',
		consultationHours: 'Consultation Hours',
		quotePrepHours: 'Quote Prep Hours',
		siteVisitCosts: 'Site Visit Costs (Eliminated with HortEra)',
		siteVisitHint: 'Customers upload photos and details, so you don\'t need to visit sites for planning',
		siteVisitsPerProject: 'Site Visits per Project',
		hoursPerVisit: 'Hours per Visit',
		avgDistanceKm: 'Average Distance to Site (km)',
		oneWayDistance: 'One-way distance',
		fuelEfficiency: 'Fuel Efficiency (L/100km)',
		fuelEfficiencyHint: 'Vehicle fuel consumption',
		fuelPrice: 'Fuel Price (€/L)',
		fuelPriceHint: 'Current fuel price',
		calculatedGasCost: 'Calculated gas cost per visit:',
		roundTrip: 'km round trip',
		additionalTimeSavings: 'Additional Time Savings',
		revisionHours: 'Revision Hours per Project',
		revisionHint: 'Better planning helps get it right the first time',
		materialSourcingHours: 'Material Sourcing Hours per Project',
		materialSourcingHint: 'Catalog integration saves time',
		softwareSubscriptions: 'Software Subscriptions (€/month)',
		softwareHint: 'CAD tools, design software, etc.',
		otherCosts: 'Other Planning Costs (€/month)',
		otherCostsHint: 'Research, sourcing, tools, etc.',
		selectPlan: 'Select HortEra Plan',
		planBasic: 'Basic',
		planPro: 'Pro',
		planPremium: 'Premium',
		priceUnit: '€ / month',
		yourSavings: 'Your Savings',
		currentMonthlyCosts: 'Current Monthly Costs:',
		gardenHubPlan: 'HortEra',
		monthlySavings: 'Monthly Savings:',
		annualSavings: 'Annual Savings',
		roi: 'ROI',
		payback: 'Payback',
		months: 'months',
		costBreakdown: 'Cost Breakdown',
		planningTime: 'Planning Time:',
		consultation: 'Consultation:',
		quotePreparation: 'Quote Preparation:',
		siteVisitsTime: 'Site Visits (Time):',
		siteVisitsGas: 'Site Visits (Gas):',
		revisions: 'Revisions:',
		materialSourcing: 'Material Sourcing:',
		software: 'Software:',
		otherCostsLabel: 'Other Costs:',
		getStarted: 'Get Started',
		signUpNow: 'Sign Up Now',
		howWeCalculate: 'How We Calculate',
		howWeCalculateText: 'Savings include eliminated site visits (customers upload photos), reduced planning time, fewer revisions, faster material sourcing (integrated catalog), and reduced software costs. HortEra streamlines your entire workflow.',
		readyToSave: 'Ready to start saving?',
		ctaSubtext: 'Join companies that are already saving time and money with HortEra. Start your free trial today.',
		switchToGerman: 'Deutsch',
		switchToEnglish: 'English',
		switchToCroatian: 'Hrvatski',
	},
	de: {
		pageTitle: 'Einsparungsrechner',
		pageSubtitle: 'Berechnen Sie, wie viel Geld und Zeit Ihr Unternehmen mit HortEra sparen kann. Keine Vor-Ort-Besuche mehr, weniger Planungszeit, weniger Überarbeitungen und einen schlanken Workflow.',
		pageHint: 'Aktuelle Kosten eingeben • Einsparungen anzeigen',
		modeBasic: 'Einfach',
		modeAdvanced: 'Erweitert',
		calculationMode: 'Berechnungsmodus',
		basicModeHint: 'Schätzt die vollen monatlichen Workflow-Kosten als potenzielle Einsparung.',
		advancedAssumptions: 'Einsparungsannahmen',
		advancedModeHint: 'Passen Sie an, wie stark HortEra jeden Kostenbereich reduziert. Standardwerte sind konservative Schätzungen.',
		reductionPct: 'Reduktion',
		planningReduction: 'Planungszeit',
		consultationReduction: 'Beratung',
		quotePrepReduction: 'Angebotsvorbereitung',
		siteVisitReduction: 'Vor-Ort-Besuche (Planung)',
		revisionReduction: 'Überarbeitungen',
		materialSourcingReduction: 'Materialbeschaffung',
		softwareReduction: 'Software-Abos',
		otherCostsReduction: 'Sonstige Planungskosten',
		returnVisitsSection: 'Nachfass- / Rückbesuche',
		returnVisitsPerProject: 'Rückbesuche pro Projekt',
		hoursPerReturnVisit: 'Stunden pro Rückbesuch',
		returnVisitReduction: 'Reduktion Rückbesuche',
		returnVisitHint: 'Zusätzliche Fahrten für Nachmessungen, Änderungen oder Klärungen nach dem ersten Besuch.',
		upliftSection: 'Kapazität & Abschluss',
		extraProjectsPerMonth: 'Zusätzliche Projekte pro Monat',
		upliftHint: 'Projekte, die Sie mit demselben Team gewinnen oder abwickeln können dank schnellerer Angebote und Planung.',
		marketPreset: 'Markt-Voreinstellungen',
		marketGermany: 'Deutschland',
		marketAustria: 'Österreich',
		marketCroatia: 'Kroatien',
		estimatedMonthlySavings: 'Geschätzte monatliche Einsparung:',
		upliftSavings: 'Kapazität / Abschluss:',
		returnVisitSavings: 'Rückbesuche:',
		returnVisitsCost: 'Rückbesuche (aktuell):',
		howWeCalculateAdvanced: 'Im erweiterten Modus werden Ihre Reduktionsprozentsätze auf jeden Kostenbereich angewendet, plus Rückbesuche und optionale Zusatzprojekte. Alle Angaben sind Schätzungen.',
		yourCurrentCosts: 'Ihre aktuellen Kosten',
		quickStart: 'Schnellstart (Voreinstellung laden)',
		smallCompany: 'Kleines Unternehmen',
		mediumCompany: 'Mittleres Unternehmen',
		largeCompany: 'Großes Unternehmen',
		workersLabel: 'Anzahl Mitarbeiter / Teammitglieder',
		projectsPerMonth: 'Projekte pro Monat',
		hourlyRate: 'Durchschnittlicher Stundensatz (€)',
		timeCostsPerProject: 'Zeitkosten pro Projekt',
		planningHours: 'Planungsstunden',
		consultationHours: 'Beratungsstunden',
		quotePrepHours: 'Angebotsvorbereitung (Stunden)',
		siteVisitCosts: 'Kosten für Vor-Ort-Besuche (mit HortEra entfallen)',
		siteVisitHint: 'Kunden laden Fotos und Details hoch – Sie müssen für die Planung nicht vor Ort sein.',
		siteVisitsPerProject: 'Vor-Ort-Besuche pro Projekt',
		hoursPerVisit: 'Stunden pro Besuch',
		avgDistanceKm: 'Durchschnittliche Entfernung (km)',
		oneWayDistance: 'Einfache Strecke',
		fuelEfficiency: 'Kraftstoffverbrauch (L/100km)',
		fuelEfficiencyHint: 'Fahrzeugverbrauch',
		fuelPrice: 'Kraftstoffpreis (€/L)',
		fuelPriceHint: 'Aktueller Kraftstoffpreis',
		calculatedGasCost: 'Berechnete Kraftstoffkosten pro Besuch:',
		roundTrip: 'km Hin- und Rückfahrt',
		additionalTimeSavings: 'Weitere Zeiteinsparungen',
		revisionHours: 'Überarbeitungsstunden pro Projekt',
		revisionHint: 'Bessere Planung hilft, es beim ersten Mal richtig zu machen',
		materialSourcingHours: 'Stunden Materialbeschaffung pro Projekt',
		materialSourcingHint: 'Katalog-Integration spart Zeit',
		softwareSubscriptions: 'Software-Abos (€/Monat)',
		softwareHint: 'CAD, Design-Software usw.',
		otherCosts: 'Sonstige Planungskosten (€/Monat)',
		otherCostsHint: 'Recherche, Beschaffung, Tools usw.',
		selectPlan: 'HortEra-Plan wählen',
		planBasic: 'Basis',
		planPro: 'Pro',
		planPremium: 'Premium',
		priceUnit: '€ / Monat',
		yourSavings: 'Ihre Einsparungen',
		currentMonthlyCosts: 'Aktuelle monatliche Kosten:',
		gardenHubPlan: 'HortEra',
		monthlySavings: 'Monatliche Einsparung:',
		annualSavings: 'Jahresersparnis',
		roi: 'ROI',
		payback: 'Amortisation',
		months: 'Monate',
		costBreakdown: 'Kostenaufstellung',
		planningTime: 'Planungszeit:',
		consultation: 'Beratung:',
		quotePreparation: 'Angebotsvorbereitung:',
		siteVisitsTime: 'Vor-Ort-Besuche (Zeit):',
		siteVisitsGas: 'Vor-Ort-Besuche (Kraftstoff):',
		revisions: 'Überarbeitungen:',
		materialSourcing: 'Materialbeschaffung:',
		software: 'Software:',
		otherCostsLabel: 'Sonstige Kosten:',
		getStarted: 'Loslegen',
		signUpNow: 'Jetzt registrieren',
		howWeCalculate: 'So berechnen wir',
		howWeCalculateText: 'Einsparungen durch entfallende Vor-Ort-Besuche (Kunden laden Fotos hoch), weniger Planungszeit, weniger Überarbeitungen, schnellere Materialbeschaffung (integrierter Katalog) und geringere Softwarekosten. HortEra optimiert Ihren gesamten Workflow.',
		readyToSave: 'Bereit zum Sparen?',
		ctaSubtext: 'Schließen Sie sich Unternehmen an, die mit HortEra bereits Zeit und Geld sparen. Starten Sie noch heute Ihre kostenlose Testversion.',
		switchToGerman: 'Deutsch',
		switchToEnglish: 'English',
		switchToCroatian: 'Hrvatski',
	},
	hr: {
		pageTitle: 'Kalkulator uštede',
		pageSubtitle: 'Izračunajte koliko novca i vremena vaša tvrtka može uštedjeti uz HortEra. Uklonite terenske obilaske, smanjite vrijeme planiranja i pojednostavite rad.',
		pageHint: 'Unesite trenutne troškove • Pogledajte uštede',
		modeBasic: 'Osnovno',
		modeAdvanced: 'Napredno',
		calculationMode: 'Način izračuna',
		basicModeHint: 'Procjenjuje puni mjesečni trošak rada kao potencijalnu uštedu.',
		advancedAssumptions: 'Pretpostavke uštede',
		advancedModeHint: 'Prilagodite koliko HortEra smanjuje svaku kategoriju troškova. Zadane vrijednosti su konzervativne procjene.',
		reductionPct: 'smanjenje',
		planningReduction: 'Vrijeme planiranja',
		consultationReduction: 'Konzultacije',
		quotePrepReduction: 'Priprema ponude',
		siteVisitReduction: 'Obilasci (planiranje)',
		revisionReduction: 'Dorade',
		materialSourcingReduction: 'Nabava materijala',
		softwareReduction: 'Softverske pretplate',
		otherCostsReduction: 'Ostali troškovi planiranja',
		returnVisitsSection: 'Ponovni / dodatni obilasci',
		returnVisitsPerProject: 'Ponovni obilasci po projektu',
		hoursPerReturnVisit: 'Sati po ponovnom obilasku',
		returnVisitReduction: 'Smanjenje ponovnih obilazaka',
		returnVisitHint: 'Dodatne vožnje za ponovna mjerenja, izmjene ili pojašnjenja nakon prvog obilaska.',
		upliftSection: 'Kapacitet i konverzija',
		extraProjectsPerMonth: 'Dodatnih projekata mjesečno',
		upliftHint: 'Projekti koje možete osvojiti ili obraditi istim timom zahvaljujući bržim ponudama i planiranju.',
		marketPreset: 'Tržišne zadane vrijednosti',
		marketGermany: 'Njemačka',
		marketAustria: 'Austrija',
		marketCroatia: 'Hrvatska',
		estimatedMonthlySavings: 'Procijenjena mjesečna ušteda:',
		upliftSavings: 'Kapacitet / konverzija:',
		returnVisitSavings: 'Ponovni obilasci:',
		returnVisitsCost: 'Ponovni obilasci (trenutno):',
		howWeCalculateAdvanced: 'Napredni način primjenjuje postotke smanjenja na svaku kategoriju, dodaje uštedu na ponovne obilaske i opcionalne dodatne projekte. Sve su to procjene.',
		yourCurrentCosts: 'Vaši trenutni troškovi',
		quickStart: 'Brzi početak (učitaj predložak)',
		smallCompany: 'Mala tvrtka',
		mediumCompany: 'Srednja tvrtka',
		largeCompany: 'Velika tvrtka',
		workersLabel: 'Broj radnika / članova tima',
		projectsPerMonth: 'Projekata mjesečno',
		hourlyRate: 'Prosječna satnica (€)',
		timeCostsPerProject: 'Troškovi vremena po projektu',
		planningHours: 'Sati planiranja',
		consultationHours: 'Sati konzultacija',
		quotePrepHours: 'Sati pripreme ponude',
		siteVisitCosts: 'Troškovi obilaska terena (uklanjaju se uz HortEra)',
		siteVisitHint: 'Klijenti učitavaju fotografije i detalje, pa terenski obilazak za planiranje često nije potreban',
		siteVisitsPerProject: 'Obilasci po projektu',
		hoursPerVisit: 'Sati po obilasku',
		avgDistanceKm: 'Prosječna udaljenost do lokacije (km)',
		oneWayDistance: 'Udaljenost u jednom smjeru',
		fuelEfficiency: 'Potrošnja goriva (L/100km)',
		fuelEfficiencyHint: 'Potrošnja vozila',
		fuelPrice: 'Cijena goriva (€/L)',
		fuelPriceHint: 'Trenutna cijena goriva',
		calculatedGasCost: 'Izračunati trošak goriva po obilasku:',
		roundTrip: 'km povratno',
		additionalTimeSavings: 'Dodatna ušteda vremena',
		revisionHours: 'Sati dorade po projektu',
		revisionHint: 'Bolje planiranje smanjuje korekcije',
		materialSourcingHours: 'Sati nabave materijala po projektu',
		materialSourcingHint: 'Integrirani katalog štedi vrijeme',
		softwareSubscriptions: 'Softverske pretplate (€/mj.)',
		softwareHint: 'CAD alati, dizajnerski softver i sl.',
		otherCosts: 'Ostali troškovi planiranja (€/mj.)',
		otherCostsHint: 'Istraživanje, nabava, alati i sl.',
		selectPlan: 'Odaberite HortEra paket',
		planBasic: 'Basic',
		planPro: 'Pro',
		planPremium: 'Premium',
		priceUnit: '€ / mjesec',
		yourSavings: 'Vaša ušteda',
		currentMonthlyCosts: 'Trenutni mjesečni troškovi:',
		gardenHubPlan: 'HortEra',
		monthlySavings: 'Mjesečna ušteda:',
		annualSavings: 'Godišnja ušteda',
		roi: 'ROI',
		payback: 'Povrat ulaganja',
		months: 'mjeseci',
		costBreakdown: 'Struktura troškova',
		planningTime: 'Vrijeme planiranja:',
		consultation: 'Konzultacije:',
		quotePreparation: 'Priprema ponude:',
		siteVisitsTime: 'Obilasci (vrijeme):',
		siteVisitsGas: 'Obilasci (gorivo):',
		revisions: 'Dorade:',
		materialSourcing: 'Nabava materijala:',
		software: 'Softver:',
		otherCostsLabel: 'Ostali troškovi:',
		getStarted: 'Kreni',
		signUpNow: 'Registriraj se',
		howWeCalculate: 'Kako računamo',
		howWeCalculateText: 'Ušteda uključuje manje terenskih obilazaka (klijenti učitavaju fotografije), manje vremena planiranja, manje dorada, bržu nabavu i niže softverske troškove.',
		readyToSave: 'Spremni za uštedu?',
		ctaSubtext: 'Pridružite se tvrtkama koje već štede vrijeme i novac uz HortEra.',
		switchToGerman: 'Deutsch',
		switchToEnglish: 'English',
		switchToCroatian: 'Hrvatski',
	},
};

const presetScenarios = [
	{
		name: 'Small Company',
		workers: 2,
		projectsPerMonth: 8,
		planningHoursPerProject: 8,
		hourlyRate: 25,
		softwareCosts: 50,
		siteVisitsPerProject: 1,
		hoursPerSiteVisit: 2,
		averageDistanceKm: 15,
		fuelEfficiencyLPer100km: 8,
		fuelPricePerLiter: 1.6,
		revisionHoursPerProject: 3,
		materialSourcingHoursPerProject: 2,
		otherCosts: 100
	},
	{
		name: 'Medium Company',
		workers: 7,
		projectsPerMonth: 18,
		planningHoursPerProject: 10,
		hourlyRate: 30,
		softwareCosts: 150,
		siteVisitsPerProject: 1,
		hoursPerSiteVisit: 2.5,
		averageDistanceKm: 25,
		fuelEfficiencyLPer100km: 9,
		fuelPricePerLiter: 1.6,
		revisionHoursPerProject: 4,
		materialSourcingHoursPerProject: 3,
		otherCosts: 200
	},
	{
		name: 'Large Company',
		workers: 15,
		projectsPerMonth: 35,
		planningHoursPerProject: 12,
		hourlyRate: 35,
		softwareCosts: 300,
		siteVisitsPerProject: 1,
		hoursPerSiteVisit: 3,
		averageDistanceKm: 35,
		fuelEfficiencyLPer100km: 10,
		fuelPricePerLiter: 1.6,
		revisionHoursPerProject: 5,
		materialSourcingHoursPerProject: 4,
		otherCosts: 400
	}
];

const marketPresets = {
	germany: { hourlyRate: 32, fuelPricePerLiter: 1.75 },
	austria: { hourlyRate: 30, fuelPricePerLiter: 1.65 },
	croatia: { hourlyRate: 18, fuelPricePerLiter: 1.55 },
} as const;

const defaultAdvancedAssumptions = {
	planningReductionPct: 40,
	consultationReductionPct: 30,
	quotePrepReductionPct: 50,
	siteVisitReductionPct: 85,
	revisionReductionPct: 50,
	materialSourcingReductionPct: 40,
	softwareReductionPct: 25,
	otherCostsReductionPct: 15,
	returnVisitsPerProject: 0.5,
	hoursPerReturnVisit: 1.5,
	returnVisitReductionPct: 70,
	extraProjectsPerMonth: 1,
};

function pct(value: number, percent: number) {
	return value * (Math.min(100, Math.max(0, percent)) / 100);
}

type ReductionRowProps = {
	label: string;
	value: number;
	onChange: (value: number) => void;
	reductionLabel: string;
};

function ReductionRow({ label, value, onChange, reductionLabel }: ReductionRowProps) {
	return (
		<div className="mb-3">
			<div className="d-flex justify-content-between align-items-center mb-1">
				<label className="form-label small mb-0">{label}</label>
				<span className="small text-muted">{value}% {reductionLabel}</span>
			</div>
			<input
				type="range"
				className="form-range"
				min={0}
				max={100}
				step={5}
				value={value}
				onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
			/>
		</div>
	);
}

export default function SavingsCalculator() {
	const { user } = useAuth();
	usePageActivityTracker('savings_calculator');
	const location = useLocation();
	const { language } = useI18n();
	const lang: Lang = language;
	const t = (key: keyof typeof translations.en) => translations[lang][key];
	const ctaHref = user ? '/app/dashboard' : '/login';
	const ctaLabel = user ? t('getStarted') : t('signUpNow');

	// Form state
	const [workers, setWorkers] = useState<number>(7);
	const [projectsPerMonth, setProjectsPerMonth] = useState<number>(18);
	const [planningHoursPerProject, setPlanningHoursPerProject] = useState<number>(10);
	const [hourlyRate, setHourlyRate] = useState<number>(30);
	const [softwareCosts, setSoftwareCosts] = useState<number>(150);
	const [consultationHoursPerProject, setConsultationHoursPerProject] = useState<number>(2);
	const [quotePrepHoursPerProject, setQuotePrepHoursPerProject] = useState<number>(3);
	
	// Site visit costs
	const [siteVisitsPerProject, setSiteVisitsPerProject] = useState<number>(1);
	const [hoursPerSiteVisit, setHoursPerSiteVisit] = useState<number>(2.5);
	const [averageDistanceKm, setAverageDistanceKm] = useState<number>(25);
	const [fuelEfficiencyLPer100km, setFuelEfficiencyLPer100km] = useState<number>(9);
	const [fuelPricePerLiter, setFuelPricePerLiter] = useState<number>(1.6);
	
	// Additional savings factors
	const [revisionHoursPerProject, setRevisionHoursPerProject] = useState<number>(4);
	const [materialSourcingHoursPerProject, setMaterialSourcingHoursPerProject] = useState<number>(3);
	
	const [otherCosts, setOtherCosts] = useState<number>(200);

	const [advancedMode, setAdvancedMode] = useState(false);
	const [planningReductionPct, setPlanningReductionPct] = useState(defaultAdvancedAssumptions.planningReductionPct);
	const [consultationReductionPct, setConsultationReductionPct] = useState(defaultAdvancedAssumptions.consultationReductionPct);
	const [quotePrepReductionPct, setQuotePrepReductionPct] = useState(defaultAdvancedAssumptions.quotePrepReductionPct);
	const [siteVisitReductionPct, setSiteVisitReductionPct] = useState(defaultAdvancedAssumptions.siteVisitReductionPct);
	const [revisionReductionPct, setRevisionReductionPct] = useState(defaultAdvancedAssumptions.revisionReductionPct);
	const [materialSourcingReductionPct, setMaterialSourcingReductionPct] = useState(defaultAdvancedAssumptions.materialSourcingReductionPct);
	const [softwareReductionPct, setSoftwareReductionPct] = useState(defaultAdvancedAssumptions.softwareReductionPct);
	const [otherCostsReductionPct, setOtherCostsReductionPct] = useState(defaultAdvancedAssumptions.otherCostsReductionPct);
	const [returnVisitsPerProject, setReturnVisitsPerProject] = useState(defaultAdvancedAssumptions.returnVisitsPerProject);
	const [hoursPerReturnVisit, setHoursPerReturnVisit] = useState(defaultAdvancedAssumptions.hoursPerReturnVisit);
	const [returnVisitReductionPct, setReturnVisitReductionPct] = useState(defaultAdvancedAssumptions.returnVisitReductionPct);
	const [extraProjectsPerMonth, setExtraProjectsPerMonth] = useState(defaultAdvancedAssumptions.extraProjectsPerMonth);

	const gasCostPerVisit = (() => {
		const roundTripDistance = averageDistanceKm * 2;
		const litersPerVisit = (roundTripDistance / 100) * fuelEfficiencyLPer100km;
		return litersPerVisit * fuelPricePerLiter;
	})();

	// Calculate current costs
	const calculateCurrentCosts = () => {
		// Time-based costs
		const planningTimeCost = planningHoursPerProject * hourlyRate * projectsPerMonth;
		const consultationCost = consultationHoursPerProject * hourlyRate * projectsPerMonth;
		const quotePrepCost = quotePrepHoursPerProject * hourlyRate * projectsPerMonth;
		
		// Site visit costs (time + gas)
		const siteVisitTimeCost = siteVisitsPerProject * hoursPerSiteVisit * hourlyRate * projectsPerMonth;
		const siteVisitGasCost = siteVisitsPerProject * gasCostPerVisit * projectsPerMonth;
		const totalSiteVisitCost = siteVisitTimeCost + siteVisitGasCost;

		const effectiveReturnVisits = advancedMode ? returnVisitsPerProject : 0;
		const returnVisitTimeCost = effectiveReturnVisits * hoursPerReturnVisit * hourlyRate * projectsPerMonth;
		const returnVisitGasCost = effectiveReturnVisits * gasCostPerVisit * projectsPerMonth;
		const totalReturnVisitCost = returnVisitTimeCost + returnVisitGasCost;
		
		// Additional savings factors
		const revisionCost = revisionHoursPerProject * hourlyRate * projectsPerMonth;
		const materialSourcingCost = materialSourcingHoursPerProject * hourlyRate * projectsPerMonth;
		
		const totalTimeCost = planningTimeCost + consultationCost + quotePrepCost + siteVisitTimeCost + returnVisitTimeCost + revisionCost + materialSourcingCost;
		const totalCurrentCosts = totalTimeCost + siteVisitGasCost + returnVisitGasCost + softwareCosts + otherCosts;
		
		return {
			planningTimeCost,
			consultationCost,
			quotePrepCost,
			siteVisitTimeCost,
			siteVisitGasCost,
			totalSiteVisitCost,
			returnVisitTimeCost,
			returnVisitGasCost,
			totalReturnVisitCost,
			revisionCost,
			materialSourcingCost,
			totalTimeCost,
			softwareCosts,
			otherCosts,
			totalCurrentCosts
		};
	};

	const perProjectAdvancedSavings = () => {
		return (
			pct(planningHoursPerProject * hourlyRate, planningReductionPct) +
			pct(consultationHoursPerProject * hourlyRate, consultationReductionPct) +
			pct(quotePrepHoursPerProject * hourlyRate, quotePrepReductionPct) +
			pct(siteVisitsPerProject * hoursPerSiteVisit * hourlyRate, siteVisitReductionPct) +
			pct(siteVisitsPerProject * gasCostPerVisit, siteVisitReductionPct) +
			pct(revisionHoursPerProject * hourlyRate, revisionReductionPct) +
			pct(materialSourcingHoursPerProject * hourlyRate, materialSourcingReductionPct)
		);
	};

	// Calculate savings
	const calculateSavings = () => {
		const costs = calculateCurrentCosts();

		if (!advancedMode) {
			const monthlySavings = costs.totalCurrentCosts;
			return {
				currentCosts: costs.totalCurrentCosts,
				monthlySavings,
				annualSavings: monthlySavings * 12,
				costBreakdown: costs,
				upliftSavings: 0,
				returnVisitSavingsAmount: 0,
			};
		}

		const workflowSavings =
			pct(costs.planningTimeCost, planningReductionPct) +
			pct(costs.consultationCost, consultationReductionPct) +
			pct(costs.quotePrepCost, quotePrepReductionPct) +
			pct(costs.siteVisitTimeCost + costs.siteVisitGasCost, siteVisitReductionPct) +
			pct(costs.revisionCost, revisionReductionPct) +
			pct(costs.materialSourcingCost, materialSourcingReductionPct) +
			pct(costs.softwareCosts, softwareReductionPct) +
			pct(costs.otherCosts, otherCostsReductionPct);

		const returnVisitSavingsAmount = pct(costs.totalReturnVisitCost, returnVisitReductionPct);
		const upliftSavings = extraProjectsPerMonth * perProjectAdvancedSavings();
		const monthlySavings = workflowSavings + returnVisitSavingsAmount + upliftSavings;

		return {
			currentCosts: costs.totalCurrentCosts,
			monthlySavings,
			annualSavings: monthlySavings * 12,
			costBreakdown: costs,
			upliftSavings,
			returnVisitSavingsAmount,
		};
	};

	const savings = calculateSavings();

	// Load preset scenario
	const loadPreset = (scenario: typeof presetScenarios[0]) => {
		setWorkers(scenario.workers);
		setProjectsPerMonth(scenario.projectsPerMonth);
		setPlanningHoursPerProject(scenario.planningHoursPerProject);
		setHourlyRate(scenario.hourlyRate);
		setSoftwareCosts(scenario.softwareCosts);
		setSiteVisitsPerProject(scenario.siteVisitsPerProject);
		setHoursPerSiteVisit(scenario.hoursPerSiteVisit);
		setAverageDistanceKm(scenario.averageDistanceKm);
		setFuelEfficiencyLPer100km(scenario.fuelEfficiencyLPer100km);
		setFuelPricePerLiter(scenario.fuelPricePerLiter);
		setRevisionHoursPerProject(scenario.revisionHoursPerProject);
		setMaterialSourcingHoursPerProject(scenario.materialSourcingHoursPerProject);
		setOtherCosts(scenario.otherCosts);
		if (scenario.name === 'Small Company') {
			setExtraProjectsPerMonth(0.5);
		} else if (scenario.name === 'Medium Company') {
			setExtraProjectsPerMonth(1);
		} else {
			setExtraProjectsPerMonth(2);
		}
	};

	const loadMarketPreset = (market: keyof typeof marketPresets) => {
		const preset = marketPresets[market];
		setHourlyRate(preset.hourlyRate);
		setFuelPricePerLiter(preset.fuelPricePerLiter);
	};

	// Save scroll position when clicking CTA
	const handleCTAClick = () => {
		sessionStorage.setItem('calculatorScrollPosition', window.scrollY.toString());
	};

	// Restore scroll position
	useEffect(() => {
		const savedScroll = sessionStorage.getItem('calculatorScrollPosition');
		if (savedScroll) {
			const scrollPosition = parseInt(savedScroll, 10);
			setTimeout(() => {
				window.scrollTo({ top: scrollPosition, behavior: 'auto' });
				sessionStorage.removeItem('calculatorScrollPosition');
			}, 50);
		}
	}, [location.pathname]);

	return (
		<>
			<section id="calculator-hero" className="landing-hero py-5 py-lg-5">
				<div className="container">
					<div className="row justify-content-center">
						<div className="col-12 col-lg-10 col-xl-8 text-center position-relative">
							<h1 className="display-4 fw-bold text-success-emphasis mb-4">
								{t('pageTitle')}
							</h1>
							<p className="lead text-muted mb-4">
								{t('pageSubtitle')}
							</p>
							<p className="text-muted small">
								{t('pageHint')}
							</p>
						</div>
					</div>
				</div>
			</section>

			<section id="calculator-form" className="landing-section py-5">
				<div className="container">
					<div className="row g-4">
						{/* Left Column: Input Form */}
						<div className="col-12 col-lg-7">
							<div className="calculator-card p-4 mb-4">
								<h2 className="h4 fw-bold text-success-emphasis mb-4">
									<i className="bi bi-calculator me-2"></i>
									{t('yourCurrentCosts')}
								</h2>

								{/* Preset Scenarios */}
								<div className="mb-4">
									<label className="form-label fw-semibold">{t('quickStart')}</label>
									<div className="d-flex flex-wrap gap-2">
										{presetScenarios.map((scenario) => {
											const presetLabel = scenario.name === 'Small Company' ? t('smallCompany') : scenario.name === 'Medium Company' ? t('mediumCompany') : t('largeCompany');
											return (
												<button
													key={scenario.name}
													type="button"
													className="btn btn-outline-success btn-sm"
													onClick={() => loadPreset(scenario)}
												>
													{presetLabel}
												</button>
											);
										})}
									</div>
								</div>

								<div className="mb-4">
									<label className="form-label fw-semibold">{t('marketPreset')}</label>
									<div className="d-flex flex-wrap gap-2">
										<button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => loadMarketPreset('germany')}>
											{t('marketGermany')}
										</button>
										<button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => loadMarketPreset('austria')}>
											{t('marketAustria')}
										</button>
										<button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => loadMarketPreset('croatia')}>
											{t('marketCroatia')}
										</button>
									</div>
								</div>

								<div className="row g-3">
									{/* Company Size */}
									<div className="col-12">
										<label className="form-label fw-semibold">
											{t('workersLabel')}
										</label>
										<input
											type="number"
											className="form-control"
											min="1"
											value={workers}
											onChange={(e) => setWorkers(parseInt(e.target.value) || 1)}
										/>
									</div>

									{/* Projects per Month */}
									<div className="col-12 col-md-6">
										<label className="form-label fw-semibold">
											{t('projectsPerMonth')}
										</label>
										<input
											type="number"
											className="form-control"
											min="1"
											value={projectsPerMonth}
											onChange={(e) => setProjectsPerMonth(parseInt(e.target.value) || 1)}
										/>
									</div>

									{/* Hourly Rate */}
									<div className="col-12 col-md-6">
										<label className="form-label fw-semibold">
											{t('hourlyRate')}
										</label>
										<input
											type="number"
											className="form-control"
											min="0"
											step="0.5"
											value={hourlyRate}
											onChange={(e) => setHourlyRate(parseFloat(e.target.value) || 0)}
										/>
									</div>

									{/* Time Costs */}
									<div className="col-12">
										<label className="form-label fw-semibold text-success-emphasis">
											{t('timeCostsPerProject')}
										</label>
									</div>
									<div className="col-12 col-md-4">
										<label className="form-label small text-muted">
											{t('planningHours')}
										</label>
										<input
											type="number"
											className="form-control"
											min="0"
											step="0.5"
											value={planningHoursPerProject}
											onChange={(e) => setPlanningHoursPerProject(parseFloat(e.target.value) || 0)}
										/>
									</div>
									<div className="col-12 col-md-4">
										<label className="form-label small text-muted">
											{t('consultationHours')}
										</label>
										<input
											type="number"
											className="form-control"
											min="0"
											step="0.5"
											value={consultationHoursPerProject}
											onChange={(e) => setConsultationHoursPerProject(parseFloat(e.target.value) || 0)}
										/>
									</div>
									<div className="col-12 col-md-4">
										<label className="form-label small text-muted">
											{t('quotePrepHours')}
										</label>
										<input
											type="number"
											className="form-control"
											min="0"
											step="0.5"
											value={quotePrepHoursPerProject}
											onChange={(e) => setQuotePrepHoursPerProject(parseFloat(e.target.value) || 0)}
										/>
									</div>

									{/* Site Visit Costs */}
									<div className="col-12 mt-3">
										<label className="form-label fw-semibold text-success-emphasis">
											<i className="bi bi-geo-alt me-2"></i>
											{t('siteVisitCosts')}
										</label>
										<p className="small text-muted mb-2">
											{t('siteVisitHint')}
										</p>
									</div>
									<div className="col-12 col-md-4">
										<label className="form-label small text-muted">
											{t('siteVisitsPerProject')}
										</label>
										<input
											type="number"
											className="form-control"
											min="0"
											step="0.5"
											value={siteVisitsPerProject}
											onChange={(e) => setSiteVisitsPerProject(parseFloat(e.target.value) || 0)}
										/>
									</div>
									<div className="col-12 col-md-4">
										<label className="form-label small text-muted">
											{t('hoursPerVisit')}
										</label>
										<input
											type="number"
											className="form-control"
											min="0"
											step="0.5"
											value={hoursPerSiteVisit}
											onChange={(e) => setHoursPerSiteVisit(parseFloat(e.target.value) || 0)}
										/>
									</div>
									<div className="col-12 col-md-4">
										<label className="form-label small text-muted">
											{t('avgDistanceKm')}
										</label>
										<input
											type="number"
											className="form-control"
											min="0"
											step="1"
											value={averageDistanceKm}
											onChange={(e) => setAverageDistanceKm(parseFloat(e.target.value) || 0)}
										/>
										<small className="text-muted">{t('oneWayDistance')}</small>
									</div>
									<div className="col-12 col-md-4">
										<label className="form-label small text-muted">
											{t('fuelEfficiency')}
										</label>
										<input
											type="number"
											className="form-control"
											min="0"
											step="0.1"
											value={fuelEfficiencyLPer100km}
											onChange={(e) => setFuelEfficiencyLPer100km(parseFloat(e.target.value) || 0)}
										/>
										<small className="text-muted">{t('fuelEfficiencyHint')}</small>
									</div>
									<div className="col-12 col-md-4">
										<label className="form-label small text-muted">
											{t('fuelPrice')}
										</label>
										<input
											type="number"
											className="form-control"
											min="0"
											step="0.01"
											value={fuelPricePerLiter}
											onChange={(e) => setFuelPricePerLiter(parseFloat(e.target.value) || 0)}
										/>
										<small className="text-muted">{t('fuelPriceHint')}</small>
									</div>
									{/* Calculated Gas Cost Display */}
									<div className="col-12">
										<div className="bg-light rounded p-2 small">
											<strong>{t('calculatedGasCost')}</strong>{' '}
											{(() => {
												const roundTrip = averageDistanceKm * 2;
												const liters = (roundTrip / 100) * fuelEfficiencyLPer100km;
												const cost = liters * fuelPricePerLiter;
												return `${cost.toFixed(2)} € (${roundTrip.toFixed(1)} ${t('roundTrip')})`;
											})()}
										</div>
									</div>

									{/* Additional Savings */}
									<div className="col-12 mt-3">
										<label className="form-label fw-semibold text-success-emphasis">
											{t('additionalTimeSavings')}
										</label>
									</div>
									<div className="col-12 col-md-6">
										<label className="form-label small text-muted">
											{t('revisionHours')}
										</label>
										<input
											type="number"
											className="form-control"
											min="0"
											step="0.5"
											value={revisionHoursPerProject}
											onChange={(e) => setRevisionHoursPerProject(parseFloat(e.target.value) || 0)}
										/>
										<small className="text-muted">{t('revisionHint')}</small>
									</div>
									<div className="col-12 col-md-6">
										<label className="form-label small text-muted">
											{t('materialSourcingHours')}
										</label>
										<input
											type="number"
											className="form-control"
											min="0"
											step="0.5"
											value={materialSourcingHoursPerProject}
											onChange={(e) => setMaterialSourcingHoursPerProject(parseFloat(e.target.value) || 0)}
										/>
										<small className="text-muted">{t('materialSourcingHint')}</small>
									</div>

									{/* Other Costs */}
									<div className="col-12 col-md-6">
										<label className="form-label fw-semibold">
											{t('softwareSubscriptions')}
										</label>
										<input
											type="number"
											className="form-control"
											min="0"
											value={softwareCosts}
											onChange={(e) => setSoftwareCosts(parseFloat(e.target.value) || 0)}
										/>
										<small className="text-muted">{t('softwareHint')}</small>
									</div>
									<div className="col-12 col-md-6">
										<label className="form-label fw-semibold">
											{t('otherCosts')}
										</label>
										<input
											type="number"
											className="form-control"
											min="0"
											value={otherCosts}
											onChange={(e) => setOtherCosts(parseFloat(e.target.value) || 0)}
										/>
										<small className="text-muted">{t('otherCostsHint')}</small>
									</div>
								</div>
							</div>

							{advancedMode && (
								<div className="calculator-card p-4 mb-4">
									<h2 className="h4 fw-bold text-success-emphasis mb-3">
										<i className="bi bi-sliders me-2"></i>
										{t('advancedAssumptions')}
									</h2>
									<ReductionRow label={t('planningReduction')} value={planningReductionPct} onChange={setPlanningReductionPct} reductionLabel={t('reductionPct')} />
									<ReductionRow label={t('consultationReduction')} value={consultationReductionPct} onChange={setConsultationReductionPct} reductionLabel={t('reductionPct')} />
									<ReductionRow label={t('quotePrepReduction')} value={quotePrepReductionPct} onChange={setQuotePrepReductionPct} reductionLabel={t('reductionPct')} />
									<ReductionRow label={t('siteVisitReduction')} value={siteVisitReductionPct} onChange={setSiteVisitReductionPct} reductionLabel={t('reductionPct')} />
									<ReductionRow label={t('revisionReduction')} value={revisionReductionPct} onChange={setRevisionReductionPct} reductionLabel={t('reductionPct')} />
									<ReductionRow label={t('materialSourcingReduction')} value={materialSourcingReductionPct} onChange={setMaterialSourcingReductionPct} reductionLabel={t('reductionPct')} />
									<ReductionRow label={t('softwareReduction')} value={softwareReductionPct} onChange={setSoftwareReductionPct} reductionLabel={t('reductionPct')} />
									<ReductionRow label={t('otherCostsReduction')} value={otherCostsReductionPct} onChange={setOtherCostsReductionPct} reductionLabel={t('reductionPct')} />

									<hr className="my-4" />
									<h3 className="h6 fw-semibold text-success-emphasis mb-2">{t('returnVisitsSection')}</h3>
									<p className="text-muted small mb-3">{t('returnVisitHint')}</p>
									<div className="row g-3 mb-3">
										<div className="col-12 col-md-4">
											<label className="form-label small text-muted">{t('returnVisitsPerProject')}</label>
											<input
												type="number"
												className="form-control"
												min="0"
												step="0.25"
												value={returnVisitsPerProject}
												onChange={(e) => setReturnVisitsPerProject(parseFloat(e.target.value) || 0)}
											/>
										</div>
										<div className="col-12 col-md-4">
											<label className="form-label small text-muted">{t('hoursPerReturnVisit')}</label>
											<input
												type="number"
												className="form-control"
												min="0"
												step="0.5"
												value={hoursPerReturnVisit}
												onChange={(e) => setHoursPerReturnVisit(parseFloat(e.target.value) || 0)}
											/>
										</div>
										<div className="col-12 col-md-4">
											<ReductionRow
												label={t('returnVisitReduction')}
												value={returnVisitReductionPct}
												onChange={setReturnVisitReductionPct}
												reductionLabel={t('reductionPct')}
											/>
										</div>
									</div>

									<hr className="my-4" />
									<h3 className="h6 fw-semibold text-success-emphasis mb-2">{t('upliftSection')}</h3>
									<p className="text-muted small mb-3">{t('upliftHint')}</p>
									<div className="col-12 col-md-6">
										<label className="form-label small text-muted">{t('extraProjectsPerMonth')}</label>
										<input
											type="number"
											className="form-control"
											min="0"
											step="0.5"
											value={extraProjectsPerMonth}
											onChange={(e) => setExtraProjectsPerMonth(parseFloat(e.target.value) || 0)}
										/>
									</div>
								</div>
							)}

						</div>

						{/* Right Column: Results */}
						<div className="col-12 col-lg-5">
							<div className="calculator-results">
								<div className="calculator-card p-4 mb-3">
									<h2 className="h4 fw-bold text-success-emphasis mb-4">
										<i className="bi bi-graph-up-arrow me-2"></i>
										{t('yourSavings')}
									</h2>

									{/* Monthly Comparison */}
									<div className="mb-4">
										<div className="d-flex justify-content-between align-items-center mb-2">
											<span className="text-muted">{t('currentMonthlyCosts')}</span>
											<span className="fw-bold text-danger">
												{savings.currentCosts.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
											</span>
										</div>
										<div className="d-flex justify-content-between align-items-center">
											<span className="fw-bold text-success-emphasis">
												{advancedMode ? t('estimatedMonthlySavings') : t('monthlySavings')}
											</span>
											<span className={`h4 fw-bold mb-0 ${savings.monthlySavings >= 0 ? 'text-success' : 'text-danger'}`}>
												{savings.monthlySavings >= 0 ? '+' : ''}
												{savings.monthlySavings.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
											</span>
										</div>
										{advancedMode && (savings.returnVisitSavingsAmount > 0 || savings.upliftSavings > 0) && (
											<div className="small text-muted mt-3">
												{savings.returnVisitSavingsAmount > 0 && (
													<div className="d-flex justify-content-between mb-1">
														<span>{t('returnVisitSavings')}</span>
														<span className="text-success">
															+{savings.returnVisitSavingsAmount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
														</span>
													</div>
												)}
												{savings.upliftSavings > 0 && (
													<div className="d-flex justify-content-between">
														<span>{t('upliftSavings')}</span>
														<span className="text-success">
															+{savings.upliftSavings.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
														</span>
													</div>
												)}
											</div>
										)}
									</div>

									{/* Annual Savings */}
									<div className="bg-success-subtle rounded-3 p-3 mb-4">
										<div className="text-center">
											<div className="text-muted small mb-1">{t('annualSavings')}</div>
											<div className={`h3 fw-bold mb-0 ${savings.annualSavings >= 0 ? 'text-success' : 'text-danger'}`}>
												{savings.annualSavings >= 0 ? '+' : ''}
												{savings.annualSavings.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
											</div>
										</div>
									</div>

									{/* Cost Breakdown */}
									<div className="mb-4">
										<h5 className="h6 fw-semibold text-success-emphasis mb-3">{t('costBreakdown')}</h5>
										<div className="small">
											<div className="d-flex justify-content-between mb-2">
												<span className="text-muted">{t('planningTime')}</span>
												<span>{savings.costBreakdown.planningTimeCost.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
											</div>
											<div className="d-flex justify-content-between mb-2">
												<span className="text-muted">{t('consultation')}</span>
												<span>{savings.costBreakdown.consultationCost.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
											</div>
											<div className="d-flex justify-content-between mb-2">
												<span className="text-muted">{t('quotePreparation')}</span>
												<span>{savings.costBreakdown.quotePrepCost.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
											</div>
											<div className="d-flex justify-content-between mb-2">
												<span className="text-muted">
													<i className="bi bi-geo-alt text-danger me-1"></i>
													{t('siteVisitsTime')}
												</span>
												<span className="text-danger">
													{savings.costBreakdown.siteVisitTimeCost.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
												</span>
											</div>
											<div className="d-flex justify-content-between mb-2">
												<span className="text-muted">
													<i className="bi bi-fuel-pump text-danger me-1"></i>
													{t('siteVisitsGas')}
												</span>
												<span className="text-danger">
													{savings.costBreakdown.siteVisitGasCost.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
												</span>
											</div>
											<div className="small text-muted mb-2 ps-3" style={{ fontSize: '0.75rem' }}>
												({averageDistanceKm} km × 2 × {fuelEfficiencyLPer100km}L/100km × {fuelPricePerLiter.toFixed(2)}€/L)
											</div>
											<div className="d-flex justify-content-between mb-2">
												<span className="text-muted">{t('revisions')}</span>
												<span>{savings.costBreakdown.revisionCost.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
											</div>
											<div className="d-flex justify-content-between mb-2">
												<span className="text-muted">{t('materialSourcing')}</span>
												<span>{savings.costBreakdown.materialSourcingCost.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
											</div>
											{advancedMode && savings.costBreakdown.totalReturnVisitCost > 0 && (
												<div className="d-flex justify-content-between mb-2">
													<span className="text-muted">{t('returnVisitsCost')}</span>
													<span>{savings.costBreakdown.totalReturnVisitCost.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
												</div>
											)}
											<div className="d-flex justify-content-between mb-2">
												<span className="text-muted">{t('software')}</span>
												<span>{savings.costBreakdown.softwareCosts.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
											</div>
											<div className="d-flex justify-content-between">
												<span className="text-muted">{t('otherCostsLabel')}</span>
												<span>{savings.costBreakdown.otherCosts.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
											</div>
										</div>
									</div>

									{/* CTA */}
									<Link
										to={ctaHref}
										onClick={handleCTAClick}
										className="btn btn-success w-100 btn-lg"
									>
										{ctaLabel}
									</Link>
								</div>

								{/* Info Card */}
								<div className="calculator-card p-4 bg-success-subtle">
									<h5 className="h6 fw-semibold text-success-emphasis mb-2">
										<i className="bi bi-info-circle me-2"></i>
										{t('howWeCalculate')}
									</h5>
									<p className="small text-muted mb-0">
										{advancedMode ? t('howWeCalculateAdvanced') : t('howWeCalculateText')}
									</p>
									<div className="d-flex align-items-center justify-content-between gap-2 mt-3 pt-3 border-top border-success-subtle">
										<span className="small text-muted mb-0">{t('calculationMode')}</span>
										<div
											className="calculator-mode-toggle calculator-mode-toggle--subtle"
											role="group"
											aria-label={t('calculationMode')}
										>
											<button
												type="button"
												className={`btn ${!advancedMode ? 'btn-success' : 'btn-outline-success'}`}
												onClick={() => setAdvancedMode(false)}
												aria-pressed={!advancedMode}
											>
												{t('modeBasic')}
											</button>
											<button
												type="button"
												className={`btn ${advancedMode ? 'btn-success' : 'btn-outline-success'}`}
												onClick={() => setAdvancedMode(true)}
												aria-pressed={advancedMode}
											>
												{t('modeAdvanced')}
											</button>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			<section id="calculator-cta" className="landing-section py-5 bg-light">
				<div className="container">
					<div className="cta-tile px-4 py-5 py-lg-5 shadow-lg position-relative overflow-hidden">
						<div className="row align-items-center g-4">
							<div className="col-lg-7">
								<h2 className="display-6 fw-bold text-success-emphasis mb-3">
									{t('readyToSave')}
								</h2>
								<p className="text-muted mb-0">
									{t('ctaSubtext')}
								</p>
							</div>
							<div className="col-lg-5 d-flex flex-column flex-sm-row gap-3 justify-content-end">
								<Link to={ctaHref} onClick={handleCTAClick} className="btn btn-success btn-lg">
									{ctaLabel}
								</Link>
							</div>
						</div>
					</div>
				</div>
			</section>
		</>
	);
}

